import { useEffect, useState } from 'react';
import areaSvg from '@/assets/svg/area.svg';
import calendarSvg from '@/assets/svg/calendar.svg';
import closeSvg from '@/assets/svg/close.svg';
import coffeeSvg from '@/assets/svg/coffee.svg';
import dashboardSvg from '@/assets/svg/dashboard.svg';
import folderSvg from '@/assets/svg/folder.svg';
import spaSvg from '@/assets/svg/spa.svg';
import trainingSvg from '@/assets/svg/training.svg';
import {
  Bar,
  BarContainer,
  CloseButton,
  ColorBox,
  ContentWrapper,
  DetailsLink,
  Earned,
  EarnedHeader,
  EarnedSection,
  FarmerInfoItem,
  FarmerSection,
  Icon,
  IncomeSection,
  KgSold,
  KgSoldHeader,
  LegendContainer,
  LegendItem,
  LivefeedLink,
  LivefeedText,
  ModalContent,
  ModalOverlay,
  PlotDetail,
  PlotName,
  PlotRow,
  PlotsSection,
  PriceBar,
  PricePerKilo,
  Row,
  RowTotal,
  SectionTitle,
  SectionWrapper,
  Tab,
  Tabs,
  TotalAmount,
  TotalRow,
  TotalText,
  Year,
  YearHeader,
} from '@/components/FarmerModal/FarmerModal.styles';
import { MobileFarmerCard } from '@/components/FarmerModal/MobileFarmerCard';
import { formatDate, isValidDate } from '@/constants/formatDate';

interface FarmerModalProps {
  data: any;
  onClose: () => void;
}

type SectionKeys = 'earned' | 'income' | 'info' | 'plots';

export const FarmerModal = ({ data, onClose }: FarmerModalProps) => {
  // Keys-only diagnostic (§12: names, not values - the values are farmer PII).
  // Confirms exactly which fields reached the popup. If lirp_price/coffee_variety/
  // plots_number/plot_size/trainings are absent here, they were stripped by the
  // boundary schema (safePinCollectionSchema), not mis-read by this component.
  // eslint-disable-next-line no-console -- intentional keys-only popup diagnostic
  console.debug('[popup] farmer property keys received:', Object.keys(data ?? {}));

  const lirpPrice = parseFloat(data.lirp_price);
  // Some cohorts (Bench Maji, Uganda) have no reference price: lirp_price is null,
  // so parseFloat → NaN. Guard it - the price bar must NEVER render "NaN" (§10).
  const hasLirp = Number.isFinite(lirpPrice);
  // moyee_price is null (Bench Maji/Uganda) or "n/a"/"0" on the public layer.
  // parseFloat(null) → NaN, which then poisoned maxPrice = Math.max(lirpPrice, NaN)
  // = NaN, collapsing the LIRP bar to zero width so it looked blank even though
  // lirp_price ("40.52") was present. Coerce any missing/non-numeric value to a
  // finite 0 so the LIRP bar always sizes off lirp_price.
  const rawMoyee = data.moyee_price;
  const moyeePrice = rawMoyee == null || rawMoyee === 'n/a' ? 0 : Number(rawMoyee) || 0;

  // Currency comes straight from the layer. It may be absent/null for some rows
  // (and the layer historically shipped without a currency field at all), so
  // guard it: show the number with no prefix rather than the literal "undefined"
  // (§10). When currency is present it prefixes as before (e.g. "KSH 78.12").
  const currencyPrefix = data.currency ? `${data.currency} ` : '';

  let totalKgSold = 0;
  let totalEarned = 0;

  const enhancedData = {
    ...data,
    yield_2022: data.yield_2022 !== undefined ? data.yield_2022 : 0,
    yield_2023: data.yield_2023 !== undefined ? data.yield_2023 : 0,
    value_2022: data.value_2022 !== undefined ? data.value_2022 : 0,
    value_2023: data.value_2023 !== undefined ? data.value_2023 : 0,
  };

  const rows: React.ReactNode[] = Object.keys(enhancedData).reduce(
    (acc: React.ReactNode[], key) => {
      if (key.startsWith('yield_')) {
        const year = key.split('_')[1];
        const kgSold = enhancedData[key];

        const earnedKey = `value_${year}`;
        const earned = enhancedData[earnedKey] || 0;

        if (kgSold !== 0 || earned !== 0) {
          totalKgSold += kgSold;
          totalEarned += earned;

          acc.push(
            <Row key={year}>
              <Year>{year}</Year>
              <KgSold>{kgSold}</KgSold>
              <Earned>{earned}</Earned>
            </Row>,
          );
        }
      }
      return acc;
    },
    [],
  );

  rows.sort((a: any, b: any) => {
    const yearA = parseInt((a as React.ReactElement).key as string);
    const yearB = parseInt((b as React.ReactElement).key as string);
    return yearB - yearA;
  });

  const actualPricePerKilo = totalKgSold > 0 ? totalEarned / totalKgSold : moyeePrice;
  const safeActual = Number.isFinite(actualPricePerKilo) ? actualPricePerKilo : 0;
  const maxPrice = Math.max(hasLirp ? lirpPrice : 0, safeActual);

  const isLIRP = Boolean(moyeePrice);

  // True tab-switching: the active tab is chosen by clicks, not scroll position.
  // Default to the Living Income tab.
  const [activeTab, setActiveTab] = useState<SectionKeys>('income');

  const txtValuesArray = (data: Record<string, any>) => {
    return Object.keys(data)
      .filter((key) => key.endsWith('.txt') && data[key] !== null)
      .map((key) => data[key]);
  };

  const crops = txtValuesArray(data);

  const isCrops = !!crops.length;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  return (
    <ModalOverlay>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <img src={closeSvg} />
        </CloseButton>
        <MobileFarmerCard data={data} onClose={onClose} />
        <ContentWrapper>
          <Tabs>
            {isLIRP && totalEarned > 0 && (
              <Tab
                onClick={() => setActiveTab('earned')}
                isActive={activeTab === 'earned'}
                isLirp={isLIRP}
              >
                Earned
              </Tab>
            )}
            <Tab onClick={() => setActiveTab('income')} isActive={activeTab === 'income'}>
              Living Income
            </Tab>
            <Tab onClick={() => setActiveTab('info')} isActive={activeTab === 'info'}>
              Farmer Info
            </Tab>
            {isCrops && (
              <Tab onClick={() => setActiveTab('plots')} isActive={activeTab === 'plots'}>
                Crops
              </Tab>
            )}
          </Tabs>
          {isLIRP && totalEarned > 0 && activeTab === 'earned' && (
            <EarnedSection>
              <SectionTitle>earned</SectionTitle>
              <SectionWrapper>
                <Row>
                  <YearHeader />
                  <KgSoldHeader>KG's SOLD</KgSoldHeader>
                  <EarnedHeader>{`${currencyPrefix}EARNED`}</EarnedHeader>
                </Row>
                <>{rows}</>
                <RowTotal>
                  <Year>Total</Year>
                  <KgSold>{totalKgSold}</KgSold>
                  <Earned>{totalEarned}</Earned>
                </RowTotal>
                <TotalRow>
                  <TotalText>Total Earned by Moyee</TotalText>
                  <TotalAmount>{currencyPrefix + totalEarned}</TotalAmount>
                  <DetailsLink href="https://circularreport.fairchain.org/" target="_blank">
                    View details
                  </DetailsLink>
                </TotalRow>
              </SectionWrapper>
            </EarnedSection>
          )}
          {activeTab === 'income' && (
            <IncomeSection>
              <SectionTitle>living income reference price</SectionTitle>
              <SectionWrapper>
                <PricePerKilo>Price per kilo of coffee cherries</PricePerKilo>
                {hasLirp || totalEarned > 0 ? (
                  <>
                    <BarContainer>
                      {hasLirp && (
                        <Bar
                          width={maxPrice > 0 ? (lirpPrice / maxPrice) * 100 : 0}
                          color="#D4911C"
                          style={{ maxWidth: '50%' }}
                        >
                          <PriceBar>{currencyPrefix + lirpPrice}</PriceBar>
                        </Bar>
                      )}
                      {totalEarned > 0 && (
                        <Bar
                          width={maxPrice > 0 ? (actualPricePerKilo / maxPrice) * 100 : 0}
                          color="#E5007D"
                        >
                          <PriceBar>{`${currencyPrefix}${actualPricePerKilo.toFixed(2)}`}</PriceBar>
                        </Bar>
                      )}
                    </BarContainer>
                    <LegendContainer>
                      {hasLirp && (
                        <LegendItem>
                          <ColorBox color="#D4911C" />
                          <span>LIRP</span>
                        </LegendItem>
                      )}
                      {totalEarned > 0 && (
                        <LegendItem>
                          <ColorBox color="#E5007D" />
                          <span>Actual Price</span>
                        </LegendItem>
                      )}
                    </LegendContainer>
                  </>
                ) : (
                  // Bench Maji / Uganda: no reference price recorded. Honest empty
                  // state (§10), never a "NaN" bar or a false 0.
                  <PricePerKilo>Not recorded for this farmer</PricePerKilo>
                )}
                {/* Moyee price is 0/null on the public layer; instead of an empty
                  value, link to live pricing in Livefeed (shown for all farmers). */}
                <LivefeedText>
                  See the Price per Kilo of Coffee Cherries paid by{' '}
                  <LivefeedLink
                    href="https://livefeed.moyeecoffee.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Moyee Livefeed Transparency platform
                  </LivefeedLink>
                </LivefeedText>
              </SectionWrapper>
            </IncomeSection>
          )}
          {activeTab === 'info' && (
            <FarmerSection>
              <SectionTitle>farmer info</SectionTitle>
              <SectionWrapper>
                {data.coffee_variety && (
                  <FarmerInfoItem>
                    <Icon src={coffeeSvg} alt="Coffee Variety" />
                    <span>Coffee Variety</span>
                    <p>{data.coffee_variety}</p>
                  </FarmerInfoItem>
                )}
                {isValidDate(data.registration_date) && (
                  <FarmerInfoItem>
                    <Icon src={calendarSvg} alt="Active Since" />
                    <span>Active Since</span>
                    {/* Reuse the shared date-only formatter (same as the pink
                        card's "Since …"): strips the trailing Z / time via string
                        slice, no timezone day-shift. */}
                    <p>{formatDate(data.registration_date)}</p>
                  </FarmerInfoItem>
                )}
                {data.plots_number && (
                  <FarmerInfoItem>
                    <Icon src={dashboardSvg} alt="Number of Plots" />
                    <span>Number of Plots</span>
                    <p>{data.plots_number} plots</p>
                  </FarmerInfoItem>
                )}
                {data.plot_size && (
                  <FarmerInfoItem>
                    <Icon src={areaSvg} alt="Area" />
                    <span>Area</span>
                    <p>{data.plot_size} HA</p>
                  </FarmerInfoItem>
                )}
                {data.seedlings_planted && (
                  <FarmerInfoItem>
                    <Icon src={spaSvg} alt="Seedlings Planted" />
                    <span>Seedlings Planted</span>
                    <p>{data.seedlings_planted}</p>
                  </FarmerInfoItem>
                )}
                {data.trainings && (
                  <FarmerInfoItem>
                    <Icon src={trainingSvg} alt="Trainings" />
                    <span>Trainings</span>
                    <p>{data.trainings}</p>
                  </FarmerInfoItem>
                )}
                {data.trainings && (
                  <FarmerInfoItem>
                    <Icon src={folderSvg} alt="Training Topics" />
                    <span>Training Topics</span>
                    <p>{data.trainings}</p>
                  </FarmerInfoItem>
                )}
              </SectionWrapper>
            </FarmerSection>
          )}
          {isCrops && activeTab === 'plots' && (
            <PlotsSection>
              <SectionTitle>Crops</SectionTitle>
              <SectionWrapper>
                {crops.map((crop, index) => (
                  <PlotRow key={index}>
                    <PlotName>Crop {index + 1}</PlotName>
                    <PlotDetail>
                      <strong>Name</strong> {crop}
                    </PlotDetail>
                  </PlotRow>
                ))}
              </SectionWrapper>
            </PlotsSection>
          )}
        </ContentWrapper>
      </ModalContent>
    </ModalOverlay>
  );
};
